import { useState, type ReactNode } from "react";
import { fmtDate, fmtNum, fmtPct, fmtRub } from "../../lib/format";
import type { PositionView } from "./usePortfolio";
import type { TradeCreate } from "./usePositionMutations";
import { useDeletePosition } from "./usePositionMutations";
import { TradeForm } from "./TradeForm";

type ActionKind = TradeCreate["trade_type"] | "delete";

type Open =
  | { positionId: number; mode: "menu" }
  | { positionId: number; mode: "form"; action: ActionKind }
  | null;

type SortKey =
  | "secid"
  | "quantity"
  | "current_clean_price"
  | "market_value"
  | "pnl"
  | "ytm"
  | "cashflow";

const COLS: { key: SortKey | null; label: string; num?: boolean }[] = [
  { key: "secid", label: "Бумага" },
  { key: "quantity", label: "Кол-во", num: true },
  { key: "current_clean_price", label: "Цена пок. → сейчас", num: true },
  { key: "market_value", label: "Стоимость", num: true },
  { key: "pnl", label: "P&L", num: true },
  { key: "ytm", label: "Net YTM", num: true },
  { key: "cashflow", label: "Ближайший cashflow" },
  { key: null, label: "" },
];

function valueFor(v: PositionView, key: SortKey): number | string | null {
  switch (key) {
    case "secid":
      return v.position.secid;
    case "quantity":
      return v.position.quantity;
    case "current_clean_price":
      return v.current_clean_price;
    case "market_value":
      return v.current_market_value_rub;
    case "pnl":
      return v.unrealized_pnl_rub;
    case "ytm":
      return v.entry_net_ytm;
    case "cashflow":
      return v.expected_total_return_rub;
  }
}

function compare(a: PositionView, b: PositionView, key: SortKey): number {
  const av = valueFor(a, key);
  const bv = valueFor(b, key);
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  if (typeof av === "string" && typeof bv === "string") return av.localeCompare(bv);
  return (av as number) - (bv as number);
}

export function PortfolioTable({
  rows,
  onPick,
}: {
  rows: PositionView[];
  onPick: (secid: string) => void;
}) {
  const [open, setOpen] = useState<Open>(null);
  const [sortKey, setSortKey] = useState<SortKey>("market_value");
  const [sortDesc, setSortDesc] = useState(true);

  const sorted = [...rows].sort((a, b) => {
    const cmp = compare(a, b, sortKey);
    return sortDesc ? -cmp : cmp;
  });

  const onHeader = (key: SortKey | null) => {
    if (key == null) return;
    if (key === sortKey) setSortDesc((d) => !d);
    else {
      setSortKey(key);
      setSortDesc(true);
    }
  };

  return (
    <table className="sk portfolio-tbl">
      <thead>
        <tr>
          {COLS.map((c, i) => (
            <Th
              key={i}
              num={c.num}
              sortable={c.key != null}
              active={c.key === sortKey}
              desc={sortDesc}
              onClick={() => onHeader(c.key)}
            >
              {c.label}
            </Th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((v) => {
          const isOpen = open?.positionId === v.position.id;
          return (
            <RowGroup
              key={v.position.id}
              v={v}
              open={isOpen ? open : null}
              onPick={() => onPick(v.position.secid)}
              onOpenMenu={() =>
                setOpen({ positionId: v.position.id, mode: "menu" })
              }
              onPickAction={(action) =>
                setOpen({ positionId: v.position.id, mode: "form", action })
              }
              onClose={() => setOpen(null)}
            />
          );
        })}
      </tbody>
    </table>
  );
}

function Th({
  children,
  num,
  sortable,
  active,
  desc,
  onClick,
}: {
  children: ReactNode;
  num?: boolean;
  sortable?: boolean;
  active?: boolean;
  desc?: boolean;
  onClick?: () => void;
}) {
  const cls = [
    num ? "num" : "",
    sortable ? "sortable" : "",
    active ? (desc ? "sort-desc" : "sort-asc") : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <th
      className={cls || undefined}
      onClick={sortable ? onClick : undefined}
      style={sortable ? { cursor: "pointer", userSelect: "none" } : undefined}
    >
      {children}
    </th>
  );
}

function RowGroup({
  v,
  open,
  onPick,
  onOpenMenu,
  onPickAction,
  onClose,
}: {
  v: PositionView;
  open: Open;
  onPick: () => void;
  onOpenMenu: () => void;
  onPickAction: (a: ActionKind) => void;
  onClose: () => void;
}) {
  const pl = v.unrealized_pnl_rub ?? 0;
  const plClass = pl > 0 ? "ok" : pl < 0 ? "bad" : "";
  const now = v.current_clean_price;
  const buy = v.position.buy_price;
  const ytm = v.entry_net_ytm;
  const expected = v.expected_total_return_rub;

  return (
    <>
      <tr style={{ cursor: "pointer" }} onClick={onPick}>
        <td>
          <div className="mono" style={{ fontSize: 12 }}>
            {v.position.secid}
          </div>
          <div>{v.bond_secname ?? v.bond_short_name ?? "—"}</div>
        </td>
        <td className="num mono">
          {v.position.quantity}
          {v.initial_quantity !== v.position.quantity && (
            <div className="soft" style={{ fontSize: 11 }}>
              из {v.initial_quantity}
            </div>
          )}
        </td>
        <td className="num">
          <div className="mono">
            {fmtNum(buy)} →{" "}
            <b>{now == null ? "—" : fmtNum(now)}</b>
          </div>
          <div className="soft mono" style={{ fontSize: 11 }}>
            {fmtDate(v.position.buy_date)}
          </div>
        </td>
        <td className="num">
          <div className="mono">
            <b>
              {v.current_market_value_rub == null
                ? "—"
                : fmtRub(v.current_market_value_rub)}
            </b>
          </div>
          <div className="soft mono" style={{ fontSize: 11 }}>
            вл. {fmtRub(v.cost_basis_rub)}
          </div>
        </td>
        <td className="num">
          <div className={`mono ${plClass}`}>
            <b>
              {pl >= 0 ? "+" : ""}{fmtRub(pl)}
            </b>
          </div>
          <div className={plClass} style={{ fontSize: 12 }}>
            {v.unrealized_pnl_pct == null
              ? ""
              : `${v.unrealized_pnl_pct >= 0 ? "+" : ""}${fmtPct(v.unrealized_pnl_pct)}`}
          </div>
          {v.realised_pnl_rub !== 0 && (
            <div
              className="soft"
              style={{ fontSize: 11 }}
              title={`продажи ${fmtRub(v.realised_sells_rub)} + купоны ${fmtRub(v.realised_coupons_rub)}`}
            >
              реал. {v.realised_pnl_rub >= 0 ? "+" : ""}{fmtRub(v.realised_pnl_rub)}
            </div>
          )}
        </td>
        <td className="num">
          {ytm == null ? "—" : <b className="accent">{fmtPct(ytm)}</b>}
        </td>
        <td>
          {expected != null && (
            <div className="row" style={{ gap: 8 }}>
              <span className="mono" style={{ fontSize: 13 }}>
                +{fmtRub(expected)}
              </span>
              {v.yield_horizon && (
                <span className="soft" style={{ fontSize: 12 }}>
                  до {fmtDate(v.yield_horizon)}
                </span>
              )}
            </div>
          )}
        </td>
        <td>
          <button
            type="button"
            className={`btn sm ${open ? "primary" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              if (open) onClose();
              else onOpenMenu();
            }}
            title="Действия"
          >
            ···
          </button>
        </td>
      </tr>
      {open && (
        <tr className="expand-row">
          <td colSpan={8}>
            <ExpandedActions
              v={v}
              open={open}
              onPickAction={onPickAction}
              onClose={onClose}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedActions({
  v,
  open,
  onPickAction,
  onClose,
}: {
  v: PositionView;
  open: NonNullable<Open>;
  onPickAction: (a: ActionKind) => void;
  onClose: () => void;
}) {
  if (open.mode === "menu") {
    return (
      <div className="actions-menu">
        <button className="btn" onClick={() => onPickAction("sell")}>
          💰 Продать
        </button>
        <button className="btn" onClick={() => onPickAction("coupon")}>
          🪙 Купон
        </button>
        <button className="btn" onClick={() => onPickAction("redeem")}>
          🏁 Погашение
        </button>
        <button className="btn" onClick={() => onPickAction("amort")}>
          ✂️ Амортизация
        </button>
        <button className="btn danger" onClick={() => onPickAction("delete")}>
          Удалить позицию
        </button>
        <button className="btn" onClick={onClose} style={{ marginLeft: "auto" }}>
          Закрыть
        </button>
      </div>
    );
  }
  if (open.action === "delete") {
    return <DeleteConfirm positionId={v.position.id} onClose={onClose} />;
  }
  return (
    <TradeForm
      positionId={v.position.id}
      kind={open.action}
      currency={v.currency}
      onClose={onClose}
    />
  );
}

function DeleteConfirm({
  positionId,
  onClose,
}: {
  positionId: number;
  onClose: () => void;
}) {
  const mutation = useDeletePosition();
  return (
    <div className="row" style={{ gap: 12 }}>
      <span>Удалить позицию вместе со всеми трейдами?</span>
      <button
        type="button"
        className="btn danger"
        disabled={mutation.isPending}
        onClick={() =>
          mutation.mutate(positionId, { onSuccess: onClose })
        }
      >
        {mutation.isPending ? "удаляю…" : "Да, удалить"}
      </button>
      <button type="button" className="btn" onClick={onClose}>
        Отмена
      </button>
      {mutation.isError && (
        <span className="status error">
          ошибка: {String(mutation.error)}
        </span>
      )}
    </div>
  );
}
